import { useState, type FormEvent } from "react";
import { Button } from "../../components/ui/Button";
import { ErrorText } from "../../components/ui/feedback";
import { Field } from "../../components/ui/Field";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { useI18n } from "../../i18n/I18nProvider";
import type { Person } from "../../lib/api-types";
import type { PersonInput } from "./mutations";
import { draftToPayload, validatePersonDraft, type PersonDraft } from "./validation";

export interface PersonModalProps {
  person?: Person | undefined;
  busy: boolean;
  error: unknown;
  onSubmit: (input: PersonInput) => void;
  onClose: () => void;
}

/** Operator-only form for a person's name and number. */
export function PersonModal({ person, busy, error, onSubmit, onClose }: PersonModalProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState<PersonDraft>({
    name: person?.name ?? "",
    number: person?.number === null || person?.number === undefined ? "" : String(person.number),
  });
  const [attempted, setAttempted] = useState(false);
  const errors = validatePersonDraft(draft);
  const hasErrors = Object.keys(errors).length > 0;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttempted(true);
    if (!hasErrors) onSubmit(draftToPayload(draft));
  };

  return (
    <Modal title={person ? t("person.editTitle") : t("person.addTitle")} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field
          label={t("person.name")}
          error={attempted && errors.name ? t(errors.name) : undefined}
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={attempted && Boolean(errors.name)}
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              maxLength={120}
              autoFocus
            />
          )}
        </Field>
        <Field
          label={t("person.number")}
          hint={t("person.numberHint")}
          error={attempted && errors.number ? t(errors.number) : undefined}
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={attempted && Boolean(errors.number)}
              value={draft.number}
              onChange={(event) => setDraft({ ...draft, number: event.target.value })}
              inputMode="numeric"
            />
          )}
        </Field>
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
